import React from 'react'
import FormControlLabel from '@mui/material/FormControlLabel'
import MenuItem from '@mui/material/MenuItem'
import Stack from '@mui/material/Stack'
import Switch from '@mui/material/Switch'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import { catalogueCourseFor, expiryLabel } from './catalogueHelpers'

export const longestExpiryMonths = (catalogueCourse) =>
  Math.max(0, ...(catalogueCourse?.pathways ?? []).map((pathway) => pathway.expiryMonths ?? 0))

// Returns an error message for the refresher period, or null when valid.
export const refresherError = (value, catalogueCourse) => {
  if (value === '' || value === null || value === undefined) return null
  const months = Number(value)
  const longest = longestExpiryMonths(catalogueCourse)
  if (!Number.isInteger(months) || months < 1 || months > longest) {
    return `Enter a whole number of months from 1 to ${longest}. The period can be shortened but not lengthened.`
  }
  return null
}

// E-learning settings of one of the organisation's courses: which catalogue
// course it is linked to and, once linked, the refresher period and
// test-out option. `value` = { catalogueCode, expiryOverrideMonths, testOutAllowed }.
const CatalogueCourseSettings = ({ catalogueCourses = [], courseId, value, onChange, disabled }) => {
  const linked = catalogueCourseFor(catalogueCourses, value.catalogueCode)
  // A catalogue course can back only one of the organisation's courses.
  const options = catalogueCourses.filter(
    (course) => !course.linkedCourse || String(course.linkedCourse._id) === String(courseId),
  )
  const monthsError = linked ? refresherError(value.expiryOverrideMonths, linked) : null

  return (
    <Stack gap={2}>
      <TextField
        select
        label="Link to e-learning course"
        value={value.catalogueCode ?? ''}
        onChange={(event) => onChange({ ...value, catalogueCode: event.target.value })}
        disabled={disabled}
        fullWidth
        helperText={
          linked
            ? 'Staff who need this course can take it as e-learning. Uploaded certificates still count.'
            : 'Link this course to let staff complete it as e-learning.'
        }
      >
        <MenuItem value="">Not linked</MenuItem>
        {options.map((course) => (
          <MenuItem key={course.code} value={course.code}>
            {course.title} ({course.code})
          </MenuItem>
        ))}
      </TextField>

      {linked && (
        <>
          <TextField
            label="Refresher period for e-learning (months)"
            type="number"
            value={value.expiryOverrideMonths ?? ''}
            onChange={(event) => onChange({ ...value, expiryOverrideMonths: event.target.value })}
            disabled={disabled}
            fullWidth
            error={!!monthsError}
            inputProps={{ min: 1, max: longestExpiryMonths(linked), step: 1, inputMode: 'numeric' }}
            helperText={
              monthsError ??
              `Leave empty to use the course's standard period. You can shorten it but not lengthen it (up to ${longestExpiryMonths(linked)} months).`
            }
          />
          <Typography variant="body2" color="text.secondary">
            Standard period:{' '}
            {linked.pathways
              .map((pathway) =>
                linked.pathways.length > 1
                  ? `${pathway.title}: ${expiryLabel(pathway.expiryMonths)}`
                  : expiryLabel(pathway.expiryMonths),
              )
              .join('; ')}
            .
          </Typography>
          <FormControlLabel
            control={
              <Switch
                checked={!!value.testOutAllowed}
                onChange={(event) => onChange({ ...value, testOutAllowed: event.target.checked })}
                disabled={disabled}
              />
            }
            label="Let staff who have completed this course before take the assessment first"
          />
        </>
      )}
    </Stack>
  )
}

export default CatalogueCourseSettings
